import { getCredentialsHeaders } from "../api/credentials.ts";
import { urls } from "../api/urls.ts";

export interface FileDownload {
  id: number;
  fileName: string | null;
}

function triggerDownloadViaIframe(url: string) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";

  iframe.onload = () => {
    iframe.remove();
  };

  iframe.src = url;
  document.body.appendChild(iframe);
}

export async function checkProjectFileExists(fileId: number): Promise<boolean> {
  const response = await fetch(`${urls.api}/projectFile/${fileId}`, {
    method: "HEAD",
    headers: getCredentialsHeaders(),
  });
  // Returns 200 if the file exists, 204 if it doesn't.
  return response.status === 200;
}

/**
 * Downloads the given project files. Does not check whether the files exist on the server.
 * Callers should use {@link checkProjectFileExists} beforehand if existence verification is needed.
 */
export function downloadProjectFile(file: FileDownload) {
  triggerDownloadViaIframe(`${urls.api}/projectFileDownload/${file.id}`);
}

export interface FileAvailabilityResult<T extends FileDownload> {
  available: T[];
  unavailable: T[];
}

/**
 * Checks all given files for availability and returns both available and unavailable files.
 */
export async function checkProjectFilesAvailability<T extends FileDownload>(
  files: T[],
): Promise<FileAvailabilityResult<T>> {
  const available: T[] = [];
  const unavailable: T[] = [];
  await Promise.all(
    files.map(async (file) => {
      if (await checkProjectFileExists(file.id)) {
        available.push(file);
      } else {
        unavailable.push(file);
      }
    }),
  );
  return { available, unavailable };
}

/**
 * Checks all given files for availability, downloads the ones that exist, and returns
 * the list of unavailable files so callers can handle notification.
 */
export async function checkAndDownloadProjectFiles(files: FileDownload[]): Promise<FileDownload[]> {
  const { available, unavailable } = await checkProjectFilesAvailability(files);
  available.forEach(downloadProjectFile);
  return unavailable;
}

export function downloadPreparedFile(fileKey: string) {
  triggerDownloadViaIframe(`${urls.api}/downloadPreparedFile/${fileKey}`);
}

export function downloadFilescannerReport() {
  triggerDownloadViaIframe(`${urls.api}/download/filescannerReport`);
}
